import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

/**
 * HTTP Interceptor that adds Authorization header to all API requests
 * Automatically includes JWT token from AuthService if available
 * Handles 401 responses by logging out and redirecting to login
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService).getLogger('auth-interceptor');
  const token = authService.getToken();

  // If token exists, clone request and add Authorization header
  if (token) {
    const clonedReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });

    return next(clonedReq).pipe(
      tap({
        error: (error) => {
          // Handle 401 Unauthorized - clear token and redirect to login
          if (error.status === 401) {
            logger.warn('🔒 Received 401 Unauthorized - logging out', {
              url: error.url,
              message: error.error?.message
            });

            authService.logout();
            router.navigate(['/login'], {
              queryParams: { expired: 'true' }
            });
          }

          // Handle 403 with the billing-lockout error shape (see server.ts's
          // `authenticate` decorator) - redirect to the billing-suspended page
          // instead of logging out, since the session itself is still valid.
          if (error.status === 403 && error.error?.error === 'Subscription inactive') {
            logger.warn('💳 Organization subscription inactive - redirecting to billing-suspended', {
              url: error.url
            });

            router.navigate(['/billing-suspended']);
          }
        }
      })
    );
  }

  // If no token, pass through original request
  return next(req);
};
